import { ArrowRight, Check, LockKeyhole } from "lucide-react";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useShareViewportFit } from "../hooks/useShareViewportFit";
import { SHARE_CONTENT_CLASS_NAME } from "./productShareLayout";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";
import ProductShareGatePanel from "./ProductShareGatePanel";
import ProductShareUnlockedHoldings, { productDetailPath } from "./ProductShareUnlockedHoldings";
import type { ShareGateFlow } from "../hooks/useProductShareGateFlow";
import type { PerformanceChartDataResult } from "../hooks/usePerformanceChartData";
import type { ProductShareMetadata } from "../services/shareableLinksService";
import type { TrackingEventProps } from "../services/trackingEvents";
import { getBenchmarkBySymbol } from "../config/benchmarkContracts";
import DetailNetBenchmarkChart from "../pages/dashboard/components/DetailNetBenchmarkChart";
import { formatNumber, formatSignedPercentage } from "../utils/numberUtils";

const HOLDINGS_SECTION_ID = "product-share-v2-holdings";
const SHARE_BENCHMARKS = [getBenchmarkBySymbol("SPX")!];

export interface TeaserHolding {
  key: string;
  type: "Stock" | "Option";
  name: string;
  description: string;
  weight: number;
}

export interface ShareViewer {
  state: "anonymous" | "loading" | "signed_in";
  email: string | null;
  landingPath: string;
}

const rowClassName = "grid min-h-12 grid-cols-[28px_minmax(0,1fr)_82px] items-center border-b border-v7-ink/10 px-3 py-3 text-sm last:border-b-0 sm:grid-cols-[48px_1fr_100px_96px] sm:px-[18px]";

function HoldingRow({ holding, index }: { holding: TeaserHolding; index: number }) {
  const isShort = holding.weight < 0;
  return (
    <div data-teaser-row className={rowClassName}>
      <span className="row-span-2 font-mono text-v7-ink/45 sm:row-span-1">{formatNumber(index + 1, 0)}</span>
      <span className="row-span-2 min-w-0 sm:row-span-1">
        <span className="block truncate font-mono font-medium text-v7-ink sm:inline">{holding.name}</span>
        <span className="block truncate text-xs text-v7-ink/60 sm:ml-2 sm:inline sm:text-[13px]">{holding.description}</span>
      </span>
      <span className={`col-start-3 row-start-2 text-right text-[10px] font-medium tracking-[0.5px] sm:row-start-1 sm:text-left sm:text-xs ${isShort ? "text-v7-rust" : "text-v7-success"}`}>
        {isShort ? "SHORT" : "LONG"}
      </span>
      <span className={`col-start-3 row-start-1 text-right font-mono font-medium sm:col-start-4 ${isShort ? "text-v7-rust" : "text-v7-success"}`}>
        {formatSignedPercentage(holding.weight)}
      </span>
    </div>
  );
}

/** Rows the public link does not disclose: numbered, blurred bars, fading out. */
function BlurredRow({ index, remaining }: { index: number; remaining: number }) {
  const opacity = "opacity-70";
  return (
    <div data-blurred-row className="relative">
      <div aria-hidden className={`${rowClassName} select-none`}>
        <span className="font-mono text-v7-ink/45">{formatNumber(index + 1, 0)}</span>
        <span className={`h-3.5 w-28 rounded bg-v7-mist blur-[3px] sm:w-48 ${opacity}`} />
        <span className={`hidden h-3.5 w-12 rounded bg-v7-mist blur-[3px] sm:block ${opacity}`} />
        <span className={`ml-auto h-3.5 w-14 rounded bg-v7-mist blur-[3px] ${opacity}`} />
      </div>
      <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-v7-paper px-3 py-1 text-xs font-medium text-v7-ink">
        +{formatNumber(remaining, 0)} more positions
      </span>
    </div>
  );
}

function UnlockedBanner({
  viewer,
  total,
  shownCount,
  isPartial,
  onContinueClick,
}: {
  viewer: ShareViewer;
  total: number;
  shownCount: number | null;
  isPartial: boolean;
  onContinueClick: () => void;
}) {
  // The share payload's position count is a cached aggregate of the published
  // version; the unlocked rows come from the live catalog. Once rows are in,
  // they are the truth for "how many", so the banner never contradicts the table.
  const summary = shownCount === null
    ? "Unlocking every position"
    : isPartial
      ? `${formatNumber(shownCount, 0)} of ${formatNumber(Math.max(total, shownCount), 0)} positions unlocked`
      : `All ${formatNumber(shownCount, 0)} positions unlocked`;

  return (
    <div
      data-testid="unlocked-banner"
      className="mb-4 flex flex-col gap-3 rounded-v7-control border border-v7-success/30 bg-v7-success/10 px-4 py-3 text-sm text-v7-ink sm:flex-row sm:items-center sm:justify-between"
    >
      <span className="flex items-start gap-2.5">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-v7-success" aria-hidden="true" />
        <span>
          {summary}
          {viewer.email ? <> for <b className="font-medium">{viewer.email}</b></> : null}.
        </span>
      </span>
      <Link
        to={viewer.landingPath}
        onClick={onContinueClick}
        className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-v7-control border border-v7-ink bg-v7-ink px-4 text-[13px] font-medium text-v7-white transition-colors hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
      >
        Continue to Plutus
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}

export interface ProductShareHoldingsCardProps {
  metadata: ProductShareMetadata;
  title: string;
  total: number;
  holdings: TeaserHolding[];
  viewer: ShareViewer;
  chart: PerformanceChartDataResult;
  flow: ShareGateFlow;
  onCtaClick: (ctaId: string, destination: string, details?: TrackingEventProps) => void;
}

/**
 * V5D holdings card: the light chart card (the shared detail chart, ink line on white), then one white card with the table
 * and the sign-up panel as its footer. Signed-in viewers get the live table
 * and the unlocked banner instead of the panel.
 */
const ProductShareHoldingsCard: React.FC<ProductShareHoldingsCardProps> = ({
  metadata,
  title,
  total,
  holdings,
  viewer,
  chart,
  flow,
  onCtaClick,
}) => {
  const isUnlocked = viewer.state === "signed_in";
  const [unlockedDetails, setUnlockedDetails] = useState<{ shownCount: number; isPartial: boolean } | null>(null);
  const handleUnlockedLoaded = useCallback((details: { shownCount: number; isPartial: boolean }) => {
    setUnlockedDetails(details);
  }, []);

  const fit = useShareViewportFit(holdings.length, total, viewer.state === "anonymous", title);
  const visibleHoldings = holdings.slice(0, fit.clearRows);
  const blurredRowCount = total > visibleHoldings.length ? 1 : 0;

  return (
    <section ref={fit.sectionRef} id={HOLDINGS_SECTION_ID} aria-label="Holdings" className="scroll-mt-24">
      <section
        data-testid="share-chart-card"
        className="mb-6 overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white p-0 pb-3 text-v7-ink sm:p-4"
      >
        <div className="relative">
          <DetailNetBenchmarkChart
            plotHeight={fit.plotHeight}
            portfolioName={title}
            chart={chart}
            visualStyle="retro"
            benchmarkSource="public"
            benchmarks={SHARE_BENCHMARKS}
            compact
            showPeriodFilters
            compactMobilePeriodFilters
            chartActionsPlacement="end"
            chartActionsAppearance="subtle"
            showWatermark
            showNet={false}
            productLabel="Gross"
            productStroke="var(--v7-ink)"
            productSwatchClassName="bg-v7-ink"
            isDarkMode={false}
            ariaLabel={`${title} performance compared with the S&P 500`}
          />
        </div>
      </section>

      {isUnlocked && (
        <UnlockedBanner
          viewer={viewer}
          total={total}
          shownCount={unlockedDetails?.shownCount ?? null}
          isPartial={unlockedDetails?.isPartial ?? false}
          onContinueClick={() => onCtaClick("unlocked_continue", "app")}
        />
      )}

      <div className="overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white">
        <div>
          <div className="min-w-0">
            <div className="grid grid-cols-[28px_minmax(0,1fr)_82px] border-b border-v7-ink/15 bg-v7-paper px-3 py-3 text-[11px] font-semibold uppercase tracking-[1.4px] text-v7-ink/60 sm:grid-cols-[48px_1fr_100px_96px] sm:px-[18px]">
              <span>#</span>
              <span>Security</span>
              <span className="hidden sm:block">Direction</span>
              <span className="text-right">Weight</span>
            </div>

            {isUnlocked ? (
              <ProductShareUnlockedHoldings
                productType={metadata.productType}
                providerId={metadata.providerId}
                productId={metadata.productId}
                providerName={metadata.providerName}
                visibility={metadata.visibility}
                total={total}
                onLoaded={handleUnlockedLoaded}
                onSubscribeClick={() => onCtaClick("unlocked_subscribe", "app")}
              />
            ) : (
              <>
                {visibleHoldings.map((holding, index) => (
                  <HoldingRow key={holding.key} holding={holding} index={index} />
                ))}
                {Array.from({ length: blurredRowCount }).map((_, index) => (
                  <BlurredRow key={`blurred-${index}`} index={visibleHoldings.length + index} remaining={total - visibleHoldings.length} />
                ))}
              </>
            )}
          </div>
        </div>

        {viewer.state === "anonymous" && (
          <ProductShareGatePanel
            flow={flow}
            total={total}
            placement="page"
            onLoginClick={() => onCtaClick("gate_login", "login")}
          />
        )}

        {isUnlocked && (
          <div className="flex items-center justify-center gap-2 border-t border-v7-ink/10 px-4 py-4 text-sm text-v7-ink/60">
            <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
            <span>
              <Link
                to={productDetailPath(metadata.productType, metadata.providerId, metadata.productId)}
                onClick={() => onCtaClick("unlocked_add_to_strategy", "app")}
                className="text-v7-ink underline underline-offset-2 hover:text-v7-bronze-dark"
              >
                Add this product to your strategy
              </Link>{" "}
              to start investing
            </span>
          </div>
        )}
      </div>
      {viewer.state === "anonymous" && <>
        <div data-share-gate-bar data-testid="share-gate-bar" aria-hidden={!fit.showBar || flow.isModalOpen}
          {...(!fit.showBar || flow.isModalOpen ? { inert: true } : {})}
          className={`fixed inset-x-0 bottom-0 z-[60] border-t border-v7-ink/15 bg-v7-paper pb-[env(safe-area-inset-bottom)] shadow-lg transition-transform duration-200 motion-reduce:transition-none ${fit.showBar && !flow.isModalOpen ? "translate-y-0" : "translate-y-full"}`}>
          <div className={`${SHARE_CONTENT_CLASS_NAME} flex min-h-[76px] items-center justify-between gap-3 py-3`}>
            <div className="min-w-0"><p className="text-sm font-medium text-v7-ink">See the whole portfolio.</p><p className="text-xs text-v7-ink/60">Free with an email.</p></div>
            <button type="button" onClick={() => {
              trackingService.track(TrackingEvents.SHARE_GATE_BAR_TAP, { surface: "product_share_v2", product_id: metadata.productId, viewport: window.innerWidth < 640 ? "phone" : "desktop" });
              flow.openModal("gate_bar_unlock");
            }} className="min-h-12 shrink-0 rounded-v7-control bg-v7-ink px-3 text-sm font-medium text-v7-white hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze sm:px-5">
              Unlock All {formatNumber(total, 0)} <span className="text-v7-bronze">Free</span>
            </button>
          </div>
        </div>
      </>}
    </section>
  );
};

export default ProductShareHoldingsCard;
